import { useMemo } from "react";
import { Box, Typography, Chip } from "@mui/material";
import dayjs from "dayjs";
import { colors } from "../../../../theme";

// Deterministic color per service name (not a fixed lookup table — the
// service names come from real conduits, not a fixed known-in-advance set).
const PALETTE = ["#D0C366", "#FEA51E", "#af696a", "#58A8AF", "#7b8bff", "#4cceac", "#c14f60", "#68BBBC"];
const colorFor = (label) => {
  let hash = 0;
  for (let i = 0; i < label.length; i++) hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length];
};

// Distinct services seen among this asset's conduits, most-recent first —
// each with how many conduits use it and when it was last seen. Purely a
// summary: no click-through, a conduit row already has everything (see the
// table below).
const ProtocolsCard = ({ conduits = [] }) => {
  const protocols = useMemo(() => {
    const byService = new Map();
    conduits.forEach(({ service, protocol, lastSeen }) => {
      const label = service || protocol || "unknown";
      let entry = byService.get(label);
      if (!entry) {
        entry = { label, count: 0, lastSeen: null };
        byService.set(label, entry);
      }
      entry.count += 1;
      if (lastSeen && (!entry.lastSeen || dayjs(lastSeen).isAfter(entry.lastSeen))) {
        entry.lastSeen = lastSeen;
      }
    });
    return [...byService.values()].sort((a, b) => b.count - a.count);
  }, [conduits]);

  return (
    <Box sx={{ borderRadius: 1.5, backgroundColor: "#1c1c1c", border: `1px solid ${colors.grey[800]}`, p: 1.5, height: "100%", overflowY: "auto" }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#459095", textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
        Protocols
      </Typography>

      {protocols.length === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: colors.grey[500], p: 1 }}>
          No known protocols in range
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
          {protocols.map(({ label, count, lastSeen }) => {
            const color = colorFor(label);
            return (
              <Box
                key={label}
                sx={{
                  display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: 1,
                  backgroundColor: "#222", border: `1px solid ${colors.grey[800]}`,
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.grey[100], minWidth: 78 }}>
                  {label}
                </Typography>
                <Chip
                  label={count}
                  size="small"
                  sx={{ bgcolor: `${color}22`, color, fontWeight: 700, fontSize: 11, height: 20 }}
                />
                <Typography sx={{ fontSize: 11.5, color: colors.grey[400], ml: "auto", whiteSpace: "nowrap" }}>
                  {lastSeen ? `Last seen: ${dayjs(lastSeen).format("YYYY-MM-DD HH:mm:ss")}` : ""}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ProtocolsCard;
